import React, { Suspense } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Box,  
  CircularProgress, // Import CircularProgress
} from "@mui/material";
import { ExpandMoreRounded } from "@mui/icons-material";
import AccelDatePicker from "../components/AccelDatePicker/AccelDatePicker";
import { NoSsr } from "@mui/base";
import dayjs, { Dayjs } from "dayjs"; // Import Dayjs
import { DesktopDatePicker, DatePicker } from "@mui/x-date-pickers";
import DashboardBox from "../components/DashboardBox/DashboardBox";

import LineItems from "../components/LineItems/LineItems";
import { useAccelContext } from "../contexts/AccelContext/AccelContext"; // Import useAccelContext
import { useAccountContext } from "../contexts/AccountsContext/AccountsContext";
import { DashboardSearch } from "../components/DashboardSearch/DashboardSearch";
import { AccelBarChart } from "../components/AccelBarChart/AccelBarChart";
import { AccelAreaChart } from "../components/AccelAreaChart/AccelAreaChart";
import ProfitAndLossBox from "../components/ProfitAndLossBox/ProfitAndLossBox";
import {
  AccelContextValues,
  AccelContextAction,
  AccelContextActionName,
} from "../types/AccelContext";
import useSWR, { useSWRConfig } from "swr";
import { useAccounts } from "../hooks/useAccounts/useAccounts";
// Lazy load the BarChart component

export const Dashboard = () => {
  const { fillerData, theme, authErrorState } = useAccelContext(); // Use context to manage selectedDate

  const { total } = useAccounts();

  if (authErrorState) {
    return null; // or return <></> for an empty fragment
  }

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      <Grid container spacing={4}>
        <Grid
          item
          container
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row",
          }}
          width="100%"
          spacing={2} // Add spacing between items
        >
          <Grid item>
            <Box sx={{ py: 1 }}>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  color: theme?.text?.hex || "#000000",
                  fontSize: 41,
                  fontFamily: "Helvetica Neue",
                }}
              >
                Accounting Home
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                pb: 4,
                fontFamily: "Helvetica Neue",
                alignItems: "flex-end", // Add this line to left-align the content
              }}
            >
              <AccelDatePicker />
            </Box>
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item container spacing={2} xs={12}>
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  backgroundColor: theme?.accent?.hex || "#000000",
                  width: "100%",
                  height: "100%",
                  padding: 0, // Set padding to 0
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      pb: 4,
                      fontFamily: "Helvetica Neue",
                      alignItems: "flex-start", // Add this line to left-align the content
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: 14,
                        fontFamily: "Helvetica Neue",
                        color: theme?.text?.hex || "#000000",
                      }}
                    >
                      Cash Balance
                    </Typography>

                    {total !== undefined ? (
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: 31,
                          fontFamily: "Helvetica Neue",
                          color: theme?.text?.hex || "#000000",
                        }}
                      >
                        ${fillerData ? 300000 : total}
                      </Typography>
                    ) : (
                      <CircularProgress
                        sx={{ color: theme?.primary?.hex || "#000000" }}
                      />
                    )}
                  </Box>

                  <NoSsr>
                    <AccelAreaChart />
                  </NoSsr>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  backgroundColor: theme?.accent?.hex || "#000000",
                  width: "100%",
                  height: "100%",
                  padding: 0, // Set padding to 0
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end", // Change this to flex-end
                    alignItems: "center",
                    height: "80%",

                    // Ensure it takes full height of the card
                  }}
                >
                  <DashboardSearch />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Second row */}
          <Grid item container spacing={2} xs={12}>
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  backgroundColor: theme?.accent?.hex || "#000000",
                  width: "100%",
                  height: "100%",
                  padding: 0, // Set padding to 0
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      pb: 8,
                      alignItems: "flex-start", // Change this to flex-start
                      fontFamily: "Helvetica Neue",
                      fontSize: "40px",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: 14,
                        fontFamily: "Helvetica Neue",
                        color: theme?.text?.hex || "#000000",
                      }}
                    >
                      Profit and Loss Chart
                    </Typography>
                  </Box>
                  <NoSsr>
                    <AccelBarChart></AccelBarChart>
                  </NoSsr>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  backgroundColor: theme?.accent?.hex || "#000000",
                  width: "100%",
                  height: "100%",
                  padding: 0,
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                >
                  <NoSsr>
                    <ProfitAndLossBox />
                  </NoSsr>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Third row */}
          <Grid item container spacing={2} xs={12}>
            <NoSsr>
              <Grid item xs={12} md={4}>
                <LineItems filterType="uncategorized" />
              </Grid>
              <Grid item xs={12} md={4}>
                <LineItems filterType="credit" />
              </Grid>
              <Grid item xs={12} md={4}>
                <LineItems filterType="debt" />
              </Grid>
            </NoSsr>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
